import { useEffect, useRef } from "react";
import { useInView, useMotionValue, animate } from "framer-motion";

/**
 * <AnimatedNumber value="+62%" />
 * Animates the numeric portion of `value` from 0 → target when scrolled into
 * view. Works with values like: "+62%", "+220/mo", "84k/mo", "+118%".
 * Writes to DOM directly via a ref to avoid React re-renders during animation.
 */
export default function AnimatedNumber({
  value,
  duration = 1.4,
  className = "",
  testId,
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const mv = useMotionValue(0);

  useEffect(() => {
    const parsed = parseValue(value);
    if (!parsed) {
      if (ref.current) ref.current.textContent = value;
      return;
    }
    const scale = parsed.isK ? "k" : parsed.isM ? "M" : "";
    const format = (n) => {
      const num =
        parsed.decimals > 0
          ? n.toFixed(parsed.decimals)
          : Math.round(n).toString();
      return `${parsed.prefix}${num}${scale}${parsed.suffix}`;
    };
    if (ref.current) ref.current.textContent = format(0);

    const unsub = mv.on("change", (latest) => {
      if (ref.current) ref.current.textContent = format(latest);
    });

    let controls;
    if (inView) {
      controls = animate(mv, parsed.target, {
        duration,
        ease: [0.22, 1, 0.36, 1],
      });
    }
    return () => {
      unsub();
      if (controls) controls.stop();
    };
  }, [inView, value, duration, mv]);

  return (
    <span ref={ref} className={className} data-testid={testId}>
      {value}
    </span>
  );
}

function parseValue(raw) {
  if (typeof raw !== "string") return null;
  const m = raw.match(/^([+\-]?)(\d+(?:\.\d+)?)([kKmM]?)(.*)$/);
  if (!m) return null;
  const [, prefix, numStr, scale, suffix] = m;
  const target = parseFloat(numStr);
  if (Number.isNaN(target)) return null;
  const decimals = numStr.includes(".") ? numStr.split(".")[1].length : 0;
  return {
    prefix: prefix || "",
    target,
    decimals,
    suffix: suffix || "",
    isK: scale === "k" || scale === "K",
    isM: scale === "m" || scale === "M",
  };
}
